import styled from 'styled-components';
import type { ViewIds } from '@ardoq/api-types';
import {
  SettingString,
  type SettingStringLevel,
  type SettingStringOptionProps,
  type SettingStringToggleProps,
} from '@ardoq/snowflakes';
import type {
  DropdownConfig,
  SettingsConfig,
  ToggleConfig,
} from '@ardoq/view-settings';
import type { BubbleChartViewSettingsRowsByRole } from './viewSettingsRowsByRole';

const RowGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

type ToOptionPropsArgs = {
  viewModifier: DropdownConfig;
  value: string | null;
  level?: SettingStringLevel;
};

const toOptionProps = ({
  viewModifier,
  value,
  level,
}: ToOptionPropsArgs): SettingStringOptionProps => ({
  type: 'option',
  level,
  title: viewModifier.label,
  iconName: viewModifier.iconName,
  info: viewModifier.popoverText,
  isDisabled: viewModifier.isDisabled,
  value,
  options: viewModifier.options,
  isKeepOpen: viewModifier.isKeepOpen,
  dropdownSize: viewModifier.dropdownSize,
  maxDropdownSize: viewModifier.maxDropdownSize,
  maxDropdownHeight: viewModifier.maxDropdownHeight,
});

type ToTogglePropsArgs = {
  viewModifier: ToggleConfig;
  viewId: ViewIds;
  level: SettingStringLevel;
};

const toToggleProps = ({
  viewModifier,
  viewId,
  level,
}: ToTogglePropsArgs): SettingStringToggleProps => ({
  type: 'toggle',
  level,
  name: viewModifier.id,
  title: viewModifier.label ?? viewModifier.id,
  iconName: viewModifier.iconName,
  info: viewModifier.popoverText,
  isDisabled: viewModifier.isDisabled,
  isChecked: viewModifier.isActive,
  onChange: isChecked =>
    viewModifier.onViewSettingsUpdate?.(
      viewId,
      { [viewModifier.id]: isChecked },
      true
    ),
});

type BubbleChartViewSettingsRowsProps = {
  rows: BubbleChartViewSettingsRowsByRole;
  viewId: ViewIds;
  getDisplayValue: (viewModifier: SettingsConfig) => string | null;
};

export const BubbleChartViewSettingsRows = ({
  rows: {
    sourceOfBubbles,
    valueSource,
    axisFields,
    showZeroSizeBubbles,
    layout,
  },
  viewId,
  getDisplayValue,
}: BubbleChartViewSettingsRowsProps) => {
  const toOptionRow = (
    viewModifier: DropdownConfig,
    level?: SettingStringLevel
  ) => (
    <SettingString
      key={viewModifier.id}
      {...toOptionProps({
        viewModifier,
        value: getDisplayValue(viewModifier),
        level,
      })}
    />
  );

  return (
    <>
      {toOptionRow(sourceOfBubbles)}
      <RowGroup role="group" aria-label={sourceOfBubbles.label}>
        {toOptionRow(valueSource, 2)}
        <RowGroup role="group" aria-label={valueSource.label}>
          {axisFields.map(axisField => toOptionRow(axisField, 3))}
        </RowGroup>
        <SettingString
          {...toToggleProps({
            viewModifier: showZeroSizeBubbles,
            viewId,
            level: 2,
          })}
        />
      </RowGroup>
      {toOptionRow(layout)}
    </>
  );
};
